<template>
  <aside 
    class="bg-white border-r border-gray-100 flex flex-col transition-all duration-300 relative z-20"
    :class="isCollapsed ? 'w-20' : 'w-[248px]'"
  >
    <!-- Logo 区域 -->
    <div class="h-20 flex items-center justify-center border-b border-gray-50 bg-white">
      <div class="flex items-center w-full px-4" v-if="!isCollapsed">
        <BrandLogo size="md" class="mr-3" />
        <div class="flex min-w-0 flex-col">
          <span class="truncate whitespace-nowrap text-gray-900 font-extrabold text-[15px] tracking-tight leading-tight">{{ dict.systemName }}</span>
          <span class="truncate text-gray-500 text-[11px] font-medium mt-1">{{ dict.systemSubtitle }}</span>
        </div>
      </div>
      <BrandLogo v-else size="lg" />
    </div>

    <!-- 菜单区域 -->
    <nav class="flex-1 overflow-y-auto py-4 custom-scrollbar">

      <!-- ① 工作台 — 独立置顶 -->
      <div class="px-2 mb-1">
        <router-link
          to="/dashboard"
          class="flex items-center px-4 py-3 mx-2 text-[13px] transition-all duration-200 group relative"
          :class="{ 
            'bg-[#6366f1] text-white font-medium rounded-xl shadow-md shadow-indigo-500/20': isActive('/dashboard'),
            'text-gray-600 font-semibold hover:text-gray-900 hover:bg-gray-50 rounded-xl': !isActive('/dashboard')
          }"
        >
          <el-icon class="text-[18px] flex-shrink-0 transition-transform group-hover:scale-110" :class="isActive('/dashboard') ? 'text-white' : 'text-gray-400'">
            <HomeFilled />
          </el-icon>
          <transition name="fade">
            <span v-if="!isCollapsed" class="ml-3 font-bold tracking-wide truncate">工作台</span>
          </transition>
          <div v-if="isCollapsed" class="sidebar-tooltip">
            工作台
            <div class="sidebar-tooltip-arrow"></div>
          </div>
        </router-link>
      </div>

      <!-- ② 产品研发 -->
      <SectionHeader
        title="产品研发"
        color="#8b5cf6"
        :collapsed="isCollapsed"
        toggleable
        :expanded="expandedSections.productRD"
        :active="isSectionActive('productRD')"
        @toggle="toggleSection('productRD')"
      />
      <ul v-show="isCollapsed || expandedSections.productRD" class="space-y-0.5 px-2">
        <SidebarItem v-for="item in productRDMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
      </ul>

      <!-- ③ 订单与计划 -->
      <SectionHeader
        title="订单与计划"
        color="#0ea5e9"
        :collapsed="isCollapsed"
        toggleable
        :expanded="expandedSections.orderPlan"
        :active="isSectionActive('orderPlan')"
        @toggle="toggleSection('orderPlan')"
      />
      <ul v-show="isCollapsed || expandedSections.orderPlan" class="space-y-0.5 px-2">
        <SidebarItem v-for="item in orderPlanMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
      </ul>

      <!-- ④ 生产执行 -->
      <SectionHeader
        title="生产执行"
        color="#10b981"
        :collapsed="isCollapsed"
        toggleable
        :expanded="expandedSections.productionExec"
        :active="isSectionActive('productionExec')"
        @toggle="toggleSection('productionExec')"
      />
      <ul v-show="isCollapsed || expandedSections.productionExec" class="space-y-0.5 px-2">
        <SidebarItem v-for="item in productionExecMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
      </ul>

      <!-- ⑤ 供应链与仓储 -->
      <SectionHeader
        title="供应链与仓储"
        color="#f59e0b"
        :collapsed="isCollapsed"
        toggleable
        :expanded="expandedSections.supplyChain"
        :active="isSectionActive('supplyChain')"
        @toggle="toggleSection('supplyChain')"
      />
      <ul v-show="isCollapsed || expandedSections.supplyChain" class="space-y-0.5 px-2">
        <SidebarItem v-for="item in supplyChainMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
      </ul>

      <!-- ⑥ 业财与结算 -->
      <SectionHeader
        title="业财与结算"
        color="#ef4444"
        :collapsed="isCollapsed"
        toggleable
        :expanded="expandedSections.finance"
        :active="isSectionActive('finance')"
        @toggle="toggleSection('finance')"
      />
      <ul v-show="isCollapsed || expandedSections.finance" class="space-y-0.5 px-2">
        <SidebarItem v-for="item in financeMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
      </ul>

      <!-- ⑦ 基础配置 — 下沉到底部，带折叠子菜单 -->
      <SectionHeader
        title="基础配置"
        color="#6b7280"
        :collapsed="isCollapsed"
        toggleable
        :expanded="expandedSections.baseConfig"
        :active="isSectionActive('baseConfig')"
        @toggle="toggleSection('baseConfig')"
      />
      <div v-show="isCollapsed || expandedSections.baseConfig" class="px-2">
        <!-- 子菜单 1: 物料与供应链 -->
        <div class="mb-0.5">
          <div
            class="flex items-center px-4 py-2.5 mx-2 rounded-xl cursor-pointer transition-all duration-200 group"
            :class="{ 'text-[#6366f1]': isSubMenuActive('supplyChainCfg'), 'text-gray-500 hover:text-gray-900 hover:bg-gray-50': !isSubMenuActive('supplyChainCfg') }"
            @click="toggleSubMenu('supplyChainCfg')"
          >
            <el-icon class="text-[18px] flex-shrink-0 transition-transform group-hover:scale-110" :class="isSubMenuActive('supplyChainCfg') ? 'text-[#6366f1]' : 'text-gray-400'">
              <Box />
            </el-icon>
            <transition name="fade">
              <span v-if="!isCollapsed" class="ml-3 text-[13px] font-bold tracking-wide truncate flex-1">物料与供应链</span>
            </transition>
            <transition name="fade">
              <el-icon v-if="!isCollapsed" class="text-xs transition-transform duration-300" :class="{ 'rotate-180': expandedMenus.supplyChainCfg }">
                <ArrowDown />
              </el-icon>
            </transition>
          </div>
          <transition name="slide">
            <ul v-show="!isCollapsed && expandedMenus.supplyChainCfg" class="mt-1 space-y-0.5 pl-4">
              <SidebarItem v-for="item in cfgSupplyChainMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
            </ul>
          </transition>
        </div>

        <!-- 子菜单 2: 制造资源 -->
        <div class="mb-0.5">
          <div
            class="flex items-center px-4 py-2.5 mx-2 rounded-xl cursor-pointer transition-all duration-200 group"
            :class="{ 'text-[#6366f1]': isSubMenuActive('mfgResource'), 'text-gray-500 hover:text-gray-900 hover:bg-gray-50': !isSubMenuActive('mfgResource') }"
            @click="toggleSubMenu('mfgResource')"
          >
            <el-icon class="text-[18px] flex-shrink-0 transition-transform group-hover:scale-110" :class="isSubMenuActive('mfgResource') ? 'text-[#6366f1]' : 'text-gray-400'">
              <FirstAidKit />
            </el-icon>
            <transition name="fade">
              <span v-if="!isCollapsed" class="ml-3 text-[13px] font-bold tracking-wide truncate flex-1">制造资源</span>
            </transition>
            <transition name="fade">
              <el-icon v-if="!isCollapsed" class="text-xs transition-transform duration-300" :class="{ 'rotate-180': expandedMenus.mfgResource }">
                <ArrowDown />
              </el-icon>
            </transition>
          </div>
          <transition name="slide">
            <ul v-show="!isCollapsed && expandedMenus.mfgResource" class="mt-1 space-y-0.5 pl-4">
              <SidebarItem v-for="item in cfgMfgResourceMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
            </ul>
          </transition>
        </div>

        <!-- 子菜单 3: 组织权限 -->
        <div class="mb-0.5">
          <div
            class="flex items-center px-4 py-2.5 mx-2 rounded-xl cursor-pointer transition-all duration-200 group"
            :class="{ 'text-[#6366f1]': isSubMenuActive('orgAuth'), 'text-gray-500 hover:text-gray-900 hover:bg-gray-50': !isSubMenuActive('orgAuth') }"
            @click="toggleSubMenu('orgAuth')"
          >
            <el-icon class="text-[18px] flex-shrink-0 transition-transform group-hover:scale-110" :class="isSubMenuActive('orgAuth') ? 'text-[#6366f1]' : 'text-gray-400'">
              <OfficeBuilding />
            </el-icon>
            <transition name="fade">
              <span v-if="!isCollapsed" class="ml-3 text-[13px] font-bold tracking-wide truncate flex-1">组织权限</span>
            </transition>
            <transition name="fade">
              <el-icon v-if="!isCollapsed" class="text-xs transition-transform duration-300" :class="{ 'rotate-180': expandedMenus.orgAuth }">
                <ArrowDown />
              </el-icon>
            </transition>
          </div>
          <transition name="slide">
            <ul v-show="!isCollapsed && expandedMenus.orgAuth" class="mt-1 space-y-0.5 pl-4">
              <SidebarItem v-for="item in cfgOrgMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
            </ul>
          </transition>
        </div>

        <!-- 子菜单 4: 数据字典 -->
        <div class="mb-0.5">
          <div
            class="flex items-center px-4 py-2.5 mx-2 rounded-xl cursor-pointer transition-all duration-200 group"
            :class="{ 'text-[#6366f1]': isSubMenuActive('dataDict'), 'text-gray-500 hover:text-gray-900 hover:bg-gray-50': !isSubMenuActive('dataDict') }"
            @click="toggleSubMenu('dataDict')"
          >
            <el-icon class="text-[18px] flex-shrink-0 transition-transform group-hover:scale-110" :class="isSubMenuActive('dataDict') ? 'text-[#6366f1]' : 'text-gray-400'">
              <Collection />
            </el-icon>
            <transition name="fade">
              <span v-if="!isCollapsed" class="ml-3 text-[13px] font-bold tracking-wide truncate flex-1">数据字典</span>
            </transition>
            <transition name="fade">
              <el-icon v-if="!isCollapsed" class="text-xs transition-transform duration-300" :class="{ 'rotate-180': expandedMenus.dataDict }">
                <ArrowDown />
              </el-icon>
            </transition>
          </div>
          <transition name="slide">
            <ul v-show="!isCollapsed && expandedMenus.dataDict" class="mt-1 space-y-0.5 pl-4">
              <SidebarItem v-for="item in cfgDictMenus" :key="item.path" v-bind="item" :collapsed="isCollapsed" />
            </ul>
          </transition>
        </div>
      </div>

    </nav>

    <!-- 底部操作区 -->
    <div class="p-4 border-t border-gray-50 bg-white">
      <div class="flex" :class="isCollapsed ? 'flex-col items-center space-y-2' : 'space-x-2'">
        <button
          @click="$emit('toggle')"
          class="flex-1 flex items-center justify-center p-2.5 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
          :title="isCollapsed ? '展开菜单' : '收起菜单'"
        >
          <el-icon class="text-xl transition-transform duration-300" :class="isCollapsed ? '' : 'rotate-180'">
            <Fold />
          </el-icon>
        </button>
        <button
          @click="handleLogout"
          class="flex-1 flex items-center justify-center p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
          title="退出登录"
        >
          <el-icon class="text-xl">
            <SwitchButton />
          </el-icon>
        </button>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { reactive, watch, computed, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDict } from '../config/dictionary'
import { hasPermission } from '../config/auth'
import { logout } from '../config/auth'
import { getConfiguredMenuSort, getConfiguredMenuTitle, isConfiguredMenuVisible } from '../config/menu'
import BrandLogo from './BrandLogo.vue'
import SidebarItem from './SidebarItem.vue'
import SectionHeader from './SectionHeader.vue'
import {
  HomeFilled, Fold, SwitchButton, ArrowDown,
  ShoppingBag, Calendar, Brush, MagicStick, Grid, List,
  Tickets, Operation, DataLine,
  TrendCharts, SetUp, Connection, Notebook, DataBoard, Scissor, Menu as MenuIcon, Van,
  ShoppingCart, DocumentAdd, Download, Upload, Sort, Coin,
  Wallet, CreditCard, Money, PieChart,
  Box, FirstAidKit, OfficeBuilding, Collection,
  User, UserFilled, Setting, Folder, CollectionTag, Reading, FullScreen, ScaleToOriginal, Printer, Checked,
  Refresh
} from '@element-plus/icons-vue'

const dict = useDict()

interface MenuItem { title: string; path: string; icon: Component; permission?: string | string[]; future?: boolean }

interface Props { isCollapsed: boolean }
const props = defineProps<Props>()
defineEmits<{ toggle: [] }>()

const route = useRoute()
const router = useRouter()

const handleLogout = () => {
  logout()
  router.push('/login')
}

/** 0.3.9: 按权限和后端菜单策略过滤菜单项 */
const menuFilter = (items: MenuItem[]) => items
  .filter(m => (!m.permission || hasPermission(m.permission)) && isConfiguredMenuVisible(m.path))
  .map(m => ({ ...m, title: getConfiguredMenuTitle(m.path) || m.title }))
  .sort((a, b) => getConfiguredMenuSort(a.path) - getConfiguredMenuSort(b.path))

// ════════════════════════════════════════════
// 菜单数据定义 — 7 大业务域
// ════════════════════════════════════════════

const _productRDMenus: MenuItem[] = [
  { title: '产品管理', path: '/parent-product', icon: ShoppingBag, permission: 'product:view' },
  { title: '版型模板库', path: '/measurement-template-management', icon: Folder, permission: 'product:view' },
  { title: '品类规格', path: '/product-category', icon: Folder, permission: 'product:view' },
  { title: '研发计划', path: '/rd-plan-workspace', icon: Calendar, permission: ['design:view', 'design:create'] },
  { title: '设计工单', path: '/design-work-orders', icon: Brush, permission: ['design:view', 'design:create'] },
  { title: '打样工单', path: '/prototype-work-orders', icon: MagicStick, permission: ['prototype:view', 'prototype:create'] },
  { title: '样衣工票', path: '/sample-work-ticket', icon: Printer, permission: ['prototype:view', 'prototype:create'] },
  { title: '样衣库', path: '/sample-library', icon: Collection, permission: 'prototype:view' },
  { title: '设计 BOM', path: '/bom-list', icon: List, permission: 'bom:view' }
]
const productRDMenus = computed(() => menuFilter(_productRDMenus))

// ③ 订单与计划
const _orderPlanMenus: MenuItem[] = [
  { title: '销售订单', path: '/sales-orders', icon: Tickets, permission: ['order:view', 'order:create'] },
  { title: '排产调度台', path: '/production-dispatch', icon: Operation, permission: 'prod:dispatch' },
  { title: '订单BOM管理', path: '/advanced-bom-workspace', icon: DataBoard, permission: 'bom:view' },
  { title: '指派中心', path: '/production-task-dispatcher', icon: Connection, permission: 'prod:dispatch' },
  { title: '质检报告管理', path: '/qc-report-management', icon: Checked, permission: ['qc:inspect', 'qc:manage', 'order:view', 'prototype:view'] },
  { title: '生产计划甘特图', path: '/production-plan-gantt', icon: DataLine, permission: 'prod:view' }
]
const orderPlanMenus = computed(() => menuFilter(_orderPlanMenus))

// ④ 生产执行
const _productionExecMenus: MenuItem[] = [
  { title: '生产进度全景', path: '/production-dashboard', icon: TrendCharts, permission: 'prod:view' },
  { title: '制造 BOM', path: '/mfg-bom-list', icon: SetUp, permission: 'bom:view' },
  { title: '工艺路线管理', path: '/routing-management', icon: Connection, permission: 'routing:manage' },
  { title: '作业指导书', path: '/work-instructions', icon: Notebook, permission: 'prod:view' },
  { title: '裁床执行台', path: '/cutting-execution', icon: Scissor, permission: 'prod:view' },
  { title: '车间扫描终端', path: '/shop-floor-execution', icon: FullScreen, permission: ['report:self', 'report:all'] },
  { title: '主任极速核验台', path: '/supervisor-rapid-report', icon: Checked, permission: 'report:all' },
  { title: '质检工作站', path: '/quality-control-station', icon: Checked, permission: ['qc:inspect', 'qc:manage'] },
  { title: '成品尾检工作台', path: '/fqc-station', icon: Checked, permission: ['qc:inspect', 'qc:manage'] },
  { title: '工序任务管理', path: '/task-board', icon: MenuIcon, permission: 'prod:view' },
  { title: '外发订单管理', path: '/outsource-order-center', icon: Van, permission: 'outsource:manage' }
]
const productionExecMenus = computed(() => menuFilter(_productionExecMenus))

// ⑤ 供应链与仓储
const _supplyChainMenus: MenuItem[] = [
  { title: '面料采购', path: '/order-procurement-center', icon: ShoppingCart, permission: ['po:view', 'po:create'] },
  { title: '辅料采购工作台', path: '/accessory-supply-workbench', icon: CollectionTag, permission: ['po:view', 'po:create', 'inventory:view', 'inventory:operate', 'warehouse:manage'] },
  { title: '物料需求池', path: '/material-requirement-pool', icon: Coin, permission: ['po:view', 'po:create'] },
  { title: '采购订单管理', path: '/purchase-order-list', icon: DocumentAdd, permission: ['po:view', 'po:create'] },
  { title: '采购收货单', path: '/receiving-list', icon: Download, permission: ['inventory:operate', 'warehouse:manage'] },
  { title: '采购退供单', path: '/purchase-return-list', icon: Refresh, permission: ['inventory:operate', 'warehouse:manage'] },
  { title: '面料入库台', path: '/fabric-inbound', icon: Upload, permission: ['inventory:operate', 'warehouse:manage'] },
  { title: '面料库存大盘', path: '/fabric-inventory', icon: DataBoard, permission: 'inventory:view' },
  { title: '仓库发料工作台', path: '/warehouse-issue', icon: Sort, permission: 'warehouse:manage' },
  { title: '库存操作中心', path: '/inventory-operation-center', icon: Refresh, permission: 'inventory:operate' },
  { title: '物料出入库明细', path: '/inventory-transaction-list', icon: DataLine, permission: 'inventory:view' },
  { title: '成品入库交接台', path: '/finished-goods-inbound', icon: Upload, permission: 'inventory:operate' },
  { title: '成品库存中心', path: '/finished-goods-hub', icon: DataBoard, permission: 'inventory:operate' }
]
const supplyChainMenus = computed(() => menuFilter(_supplyChainMenus))

// ⑥ 业财与结算
const _financeMenus: MenuItem[] = [
  { title: '供应商应付中心', path: '/supplier-finance-center', icon: Wallet, permission: 'finance:view' },
  { title: '外协厂账款', path: '/outsource-finance-center', icon: CreditCard, permission: 'finance:view' },
  { title: '客户应收中心', path: '/customer-receivable-center', icon: Tickets, permission: 'finance:view' },
  { title: '员工工资核算', path: '/payroll-management', icon: Coin, permission: 'finance:view' },
  { title: '商品成本决算', path: '/cost-decision-center', icon: PieChart, permission: 'finance:view' }
]
_financeMenus.unshift({ title: '费用管理', path: '/fee-management', icon: Money, permission: 'finance:view' })
const financeMenus = computed(() => menuFilter(_financeMenus))

// ⑦ 基础配置 — 4 个折叠子菜单
const _cfgSupplyChainMenus: MenuItem[] = [
  { title: '物料档案', path: '/material-master', icon: Box, permission: ['material:view', 'material:manage'] },
  { title: '供应商档案', path: '/supplier', icon: User, permission: 'supplier:manage' },
  { title: '客户档案', path: '/customer-management', icon: UserFilled, permission: 'customer:manage' },
  { title: '仓库管理', path: '/warehouse', icon: OfficeBuilding, permission: 'warehouse:manage' }
]
const cfgSupplyChainMenus = computed(() => menuFilter(_cfgSupplyChainMenus))

const _cfgMfgResourceMenus: MenuItem[] = [
  { title: '车间与产线', path: '/workshop-management', icon: TrendCharts, permission: 'prod:view' },
  { title: '标准工序库', path: '/standard-operation', icon: Setting, permission: 'routing:manage' },
  { title: '工人档案', path: '/worker-management', icon: UserFilled, permission: 'worker:manage' }
]
const cfgMfgResourceMenus = computed(() => menuFilter(_cfgMfgResourceMenus))

const _cfgOrgMenus: MenuItem[] = [
  { title: '组织架构', path: '/org-management', icon: OfficeBuilding, permission: 'sys:org' },
  { title: '员工档案', path: '/employee-management', icon: User, permission: 'sys:employee' },
  { title: '角色权限', path: '/role-management', icon: Setting, permission: 'sys:role' },
  { title: '菜单管理', path: '/menu-management', icon: MenuIcon, permission: 'system:menu:view' }
]
const cfgOrgMenus = computed(() => menuFilter(_cfgOrgMenus))

const _cfgDictMenus: MenuItem[] = [
  { title: '全系统颜色池', path: '/color-dictionary-management', icon: CollectionTag },
  { title: '标准尺码组管理', path: '/size-group-management', icon: Folder },
  { title: '分类元数据', path: '/category-metadata-management', icon: Folder },
  { title: '类目-部位绑定', path: '/category-pom-binding-management', icon: Connection },
  { title: '规格组合模板', path: '/spec-combo-template-management', icon: Grid },
  { title: '洗护图标与组合库', path: '/care-instruction-library', icon: CollectionTag },
  { title: '规格属性字典', path: '/global-spec-dict', icon: Reading },
  { title: '部位测量字典', path: '/pom-dictionary', icon: FullScreen },
  { title: '工序分类字典', path: '/operation-category', icon: Folder },
  { title: '单位字典', path: '/unit-dict', icon: ScaleToOriginal },
  { title: '单位换算规则', path: '/unit-conversion', icon: Sort },
  { title: '质检项目库', path: '/qc-project-dictionary', icon: Checked, permission: ['qc:manage', 'sys:dict'] },
  { title: '质检模板', path: '/qc-template-management', icon: Reading, permission: ['qc:manage', 'sys:dict'] },
  { title: '系统字典管理', path: '/sys-dict-management', icon: Collection, permission: 'sys:dict' },
  { title: '标签模板与打印', path: '/label-template-center', icon: Printer, permission: ['label-template:view', 'label-print:view', 'label-print:execute', 'sys:dict'] }
]
const cfgDictMenus = computed(() => menuFilter(
  _cfgDictMenus.map(item => item.permission ? item : { ...item, permission: 'sys:dict' })
))

// ════════════════════════════════════════════
// 一级分组与基础配置子菜单折叠逻辑
// ════════════════════════════════════════════

const expandedSections = reactive({
  productRD: true,
  orderPlan: true,
  productionExec: true,
  supplyChain: true,
  finance: true,
  baseConfig: true
})

const toggleSection = (key: keyof typeof expandedSections) => {
  expandedSections[key] = !expandedSections[key]
}

const getSectionMap = (): Record<string, { title: string; path: string }[]> => ({
  productRD: productRDMenus.value,
  orderPlan: orderPlanMenus.value,
  productionExec: productionExecMenus.value,
  supplyChain: supplyChainMenus.value,
  finance: financeMenus.value,
  baseConfig: Object.values(getSubMenuMap()).flat()
})

const expandedMenus = reactive({
  supplyChainCfg: false,
  mfgResource: false,
  orgAuth: false,
  dataDict: false
})

const toggleSubMenu = (key: keyof typeof expandedMenus) => {
  expandedMenus[key] = !expandedMenus[key]
}

watch(() => props.isCollapsed, (collapsed) => {
  if (collapsed) {
    expandedMenus.supplyChainCfg = false
    expandedMenus.mfgResource = false
    expandedMenus.orgAuth = false
    expandedMenus.dataDict = false
  }
})

const getSubMenuMap = (): Record<string, { title: string; path: string }[]> => ({
  supplyChainCfg: cfgSupplyChainMenus.value,
  mfgResource: cfgMfgResourceMenus.value,
  orgAuth: cfgOrgMenus.value,
  dataDict: cfgDictMenus.value
})

const isSubMenuActive = (key: string) => {
  return getSubMenuMap()[key]?.some(m => m.path === route.path) ?? false
}

const isSectionActive = (key: string) => {
  return getSectionMap()[key]?.some(m => m.path === route.path) ?? false
}

watch(() => route.path, (newPath) => {
  for (const [key, menus] of Object.entries(getSectionMap())) {
    if ((menus as any[]).some(m => m.path === newPath)) {
      expandedSections[key as keyof typeof expandedSections] = true
    }
  }
  for (const [key, menus] of Object.entries(getSubMenuMap())) {
    if ((menus as any[]).some(m => m.path === newPath)) {
      expandedSections.baseConfig = true
      expandedMenus[key as keyof typeof expandedMenus] = true
    }
  }
}, { immediate: true })

const isActive = (path: string) => route.path === path
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  opacity: 1;
  overflow: hidden;
}
.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
.rotate-180 {
  transform: rotate(180deg);
}
.sidebar-tooltip {
  position: fixed;
  left: 5rem;
  margin-left: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #111827;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 0.5rem;
  opacity: 0;
  visibility: hidden;
  transition-property: all;
  transition-duration: 300ms;
  white-space: nowrap;
  z-index: 50;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
.group:hover .sidebar-tooltip {
  opacity: 1;
  visibility: visible;
}
.sidebar-tooltip-arrow {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(-0.25rem, -50%);
  border: 4px solid transparent;
  border-right-color: #111827;
}
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
</style>
